/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';

import { IMenuItemData, IYfgFooterData, MenuTypeIndexId } from './yfg-footer.data';
import { ITitleData, IYfgFooterProps } from './yfg-footer.props.autogenerated';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import { IImageData } from '@msdyn365-commerce/core';

export interface SocialNetwork {
    url: string;
    icon: IImageData;
}

export interface FooterTitle {
    text: string;
    fontSize: number;
}

export interface IYfgFooterViewProps extends IYfgFooterProps<IYfgFooterData> {
    subCategoriesItemList: IMenuItemData[];
    subscribe?: React.ReactNode;
    SubscribeContainerProps: INodeProps;
    iframe?: React.ReactNode;
    socialNetworks: SocialNetwork[];
    navigationItems: React.ReactNode[];
    title?: ITitleData;
}

/**
 *
 * YfgFooter component
 * @extends {React.PureComponent<IYfgFooterProps<IYfgFooterData>>}
 */
class YfgFooter extends React.PureComponent<IYfgFooterProps<IYfgFooterData>> {
    private static RSItemIdInit: number = MenuTypeIndexId.Retail;

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }
        return nameTranslation && nameTranslation.Text;
    }

    private _mapData(): IMenuItemData[] {
        const { data } = this.props;
        const locale =
            this.props.context && this.props.context.request && this.props.context.request.locale
                ? this.props.context.request.locale
                : 'en-us';
        const mergedData: IMenuItemData[] = [];
        const forbiddenSubstring: string = '_hidden';

        if (data && data.categories && data.categories.result && data.categories.result.length > 0) {
            data.categories.result.forEach((item: CategoryHierarchy, index) => {
                const friendlyCategoryName: string = YfgFooter.getFriendlyName(locale, item?.NameTranslations) || '';
                // 1 - Eliminamos las categorías _hidden
                if (friendlyCategoryName.toUpperCase().includes(forbiddenSubstring.toUpperCase())) {
                    return;
                }

                // 2 - Si la categoría tiene subcategorías
                if (item?.Children?.length > 0) {
                    item.Children.forEach(c => {
                        const linkText: string = YfgFooter.getFriendlyName(locale, c?.NameTranslations) || '';
                        // 2.1 - Ignoramos subcategorias con _hidden
                        if (linkText.toUpperCase().includes(forbiddenSubstring.toUpperCase())) {
                            return;
                        }
                        mergedData.push({
                            id: YfgFooter.RSItemIdInit++,
                            linkText,
                            linkURL: c?.Url
                        });
                    });
                }
            });
        }
        return mergedData;
    }

    public render(): JSX.Element | null {
        const {
            slots: { subscribe, iframe, navigationItems },
            config
        } = this.props;
        const menuItemList = this._mapData();
        const viewProps: IYfgFooterViewProps = {
            ...this.props,
            subCategoriesItemList: menuItemList,
            subscribe,
            SubscribeContainerProps: {
                className: 'yfg-footer-subscribe-2'
            },
            iframe,
            socialNetworks: config?.socialArray?.map(sa => ({ url: sa.url, icon: sa.icon })) ?? [],
            title: config.title,
            navigationItems
        };
        return this.props.renderView(viewProps);
    }
}

export default YfgFooter;
